<template>
  <StatsWidget
    :title="$t('stats_dashboard.margin_totals.title')"
    :description="$t('stats_dashboard.margin_totals.description')"
    icon="calculator"
    tone="green"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasData"
    :span-full="true"
    :empty-icon="'calculator'"
    :empty-message="$t('stats_dashboard.margin_totals.empty')"
    @refresh="$emit('refresh')"
  >
    <div class="stats-margin-totals">
      <!-- Hero: the headline marge-percentage -->
      <div class="stats-margin-totals__hero">
        <div class="stats-margin-totals__hero-main">
          <span class="stats-margin-totals__hero-label">
            {{ $t('stats_dashboard.margin_totals.headline_label') }}
          </span>
          <span class="stats-margin-totals__hero-value">
            {{ formatPercent(current.margin_pct) }}
          </span>
          <span class="stats-margin-totals__hero-sub">
            {{
              $t('stats_dashboard.margin_totals.margin_on_revenue', {
                margin: formatCurrency(current.margin_value),
                revenue: formatCurrency(current.revenue),
              })
            }}
          </span>
          <span class="stats-margin-totals__hero-delta" :class="deltaClass">
            {{ marginDeltaLabel }}
          </span>
        </div>

        <!-- Coverage chip — always visible, becomes more prominent
             when coverage is low so the user understands the
             confidence interval at a glance. -->
        <div class="stats-margin-totals__coverage" :class="coverageClass">
          <BaseIcon :name="coverageIcon" size="14" />
          <div class="stats-margin-totals__coverage-text">
            <span class="stats-margin-totals__coverage-title">
              {{ coverageTitle }}
            </span>
            <span class="stats-margin-totals__coverage-detail">
              {{ coverageDetail }}
            </span>
          </div>
        </div>
      </div>

      <!-- Trend strip: current vs. previous period numbers side-by-side -->
      <div class="stats-margin-totals__trend">
        <article class="stats-margin-totals__cell">
          <span class="stats-margin-totals__cell-label">
            {{ $t('stats_dashboard.margin_totals.cells.revenue') }}
          </span>
          <span class="stats-margin-totals__cell-value">
            {{ formatCurrency(current.revenue) }}
          </span>
          <span class="stats-margin-totals__cell-delta" :class="trendClass(trends.revenue_pct)">
            {{ formatPercentDelta(trends.revenue_pct) }}
          </span>
        </article>

        <article class="stats-margin-totals__cell">
          <span class="stats-margin-totals__cell-label">
            {{ $t('stats_dashboard.margin_totals.cells.cost') }}
          </span>
          <span class="stats-margin-totals__cell-value">
            {{ formatCurrency(current.cost) }}
          </span>
          <span
            class="stats-margin-totals__cell-delta"
            :class="trendClass(trends.cost_pct, { invert: true })"
          >
            {{ formatPercentDelta(trends.cost_pct) }}
          </span>
        </article>

        <article class="stats-margin-totals__cell">
          <span class="stats-margin-totals__cell-label">
            {{ $t('stats_dashboard.margin_totals.cells.margin_value') }}
          </span>
          <span class="stats-margin-totals__cell-value">
            {{ formatCurrency(current.margin_value) }}
          </span>
          <span
            class="stats-margin-totals__cell-delta"
            :class="trendClass(trends.margin_value_pct)"
          >
            {{ formatPercentDelta(trends.margin_value_pct) }}
          </span>
        </article>

        <article class="stats-margin-totals__cell">
          <span class="stats-margin-totals__cell-label">
            {{ $t('stats_dashboard.margin_totals.cells.previous_margin_pct') }}
          </span>
          <span class="stats-margin-totals__cell-value">
            {{ formatPercent(previous.margin_pct) }}
          </span>
          <span class="stats-margin-totals__cell-delta-subtle">
            {{ previousSubLabel }}
          </span>
        </article>
      </div>
    </div>
  </StatsWidget>
</template>

<script>
  /**
   * Headline brutomarge tile — first widget in the margin block.
   *
   * Shows the period's margin% as a hero number plus an explicit
   * absolute amount ("€375 op €1.500"), the percent-point delta
   * vs. the directly preceding period, and a coverage badge that
   * tells the user how much of their data the calculation rests on.
   *
   * Data-coverage behaviour (per backend spec):
   *   • coverage_pct == null → no snapshots yet → render empty state
   *   • coverage_pct < 50    → show amber "limited data" coverage chip
   *   • coverage_pct >= 50   → show subtle green coverage chip
   *   • margin_pct_delta == null → render "—" (no previous period)
   */
  import { computed, getCurrentInstance } from 'vue';
  import StatsWidget from './StatsWidget.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import {
    formatCurrency,
    formatPercent,
    formatPercentDelta,
    formatPercentPointsDelta,
    formatCoveragePct,
    formatInteger,
  } from '@/utils/statsFormat';

  const COVERAGE_WARNING_THRESHOLD = 50;

  export default {
    name: 'StatsMarginTotals',

    components: { StatsWidget, BaseIcon },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const current = computed(() => props.widget.data?.current || {});
      const previous = computed(() => props.widget.data?.previous || {});
      const trends = computed(() => props.widget.data?.trends || {});

      // We consider the widget "data-bearing" the moment there's at
      // least one snapshot in the current period. If both periods
      // have zero coverage, the empty-state is more useful than a
      // wall of "—" placeholders.
      const hasData = computed(() => {
        const c = current.value;
        if (!c) return false;
        const covered = Number(c.covered_count) || 0;
        return covered > 0 || (Number(c.line_count) || 0) > 0;
      });

      const coveragePct = computed(() => {
        const v = current.value.coverage_pct;
        return v == null ? null : Number(v);
      });

      const coverageClass = computed(() => {
        const pct = coveragePct.value;
        if (pct == null) return 'stats-margin-totals__coverage--muted';
        if (pct < COVERAGE_WARNING_THRESHOLD) return 'stats-margin-totals__coverage--warn';
        return 'stats-margin-totals__coverage--ok';
      });

      const coverageIcon = computed(() => {
        const pct = coveragePct.value;
        if (pct == null || pct < COVERAGE_WARNING_THRESHOLD) return 'info';
        return 'check-circle';
      });

      const coverageTitle = computed(() => {
        const pct = coveragePct.value;
        if (pct == null) return $t('stats_dashboard.margin_totals.coverage.unknown_title');
        if (pct < COVERAGE_WARNING_THRESHOLD)
          return $t('stats_dashboard.margin_totals.coverage.warn_title', {
            pct: formatCoveragePct(pct),
          });
        return $t('stats_dashboard.margin_totals.coverage.ok_title', {
          pct: formatCoveragePct(pct),
        });
      });

      const coverageDetail = computed(() =>
        $t('stats_dashboard.margin_totals.coverage.detail', {
          covered: formatInteger(current.value.covered_count),
          total: formatInteger(current.value.line_count),
        })
      );

      const marginDeltaLabel = computed(() => {
        const delta = trends.value.margin_pct_delta;
        if (delta == null) {
          return $t('stats_dashboard.margin_totals.no_previous_period');
        }
        return $t('stats_dashboard.margin_totals.delta_vs_previous', {
          delta: formatPercentPointsDelta(delta),
        });
      });

      // Hero delta colour: green when margin% improved, red when it
      // dropped, neutral when null/0. We don't invert here — margin%
      // up is unambiguously good.
      const deltaClass = computed(() => {
        const delta = trends.value.margin_pct_delta;
        if (delta == null) return 'stats-margin-totals__hero-delta--neutral';
        if (delta > 0) return 'stats-margin-totals__hero-delta--up';
        if (delta < 0) return 'stats-margin-totals__hero-delta--down';
        return 'stats-margin-totals__hero-delta--neutral';
      });

      // Per-cell trend colour. For "cost" we invert: rising cost is
      // bad, so it should render red even though numerically up.
      const trendClass = (value, { invert = false } = {}) => {
        if (value == null) return 'stats-margin-totals__cell-delta--neutral';
        const effective = invert ? -value : value;
        if (effective > 0) return 'stats-margin-totals__cell-delta--up';
        if (effective < 0) return 'stats-margin-totals__cell-delta--down';
        return 'stats-margin-totals__cell-delta--neutral';
      };

      const previousSubLabel = computed(() => {
        const rev = Number(previous.value.revenue) || 0;
        if (rev <= 0) {
          return $t('stats_dashboard.margin_totals.no_previous_period_short');
        }
        return $t('stats_dashboard.margin_totals.previous_period_revenue', {
          revenue: formatCurrency(previous.value.revenue),
        });
      });

      return {
        current,
        previous,
        trends,
        hasData,
        coverageClass,
        coverageIcon,
        coverageTitle,
        coverageDetail,
        marginDeltaLabel,
        deltaClass,
        trendClass,
        previousSubLabel,
        formatCurrency,
        formatPercent,
        formatPercentDelta,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-margin-totals {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 1;
    min-height: 0;

    // ---------- Hero block ----------

    &__hero {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 1rem;
      align-items: flex-start;
      padding: 1rem 1.125rem;
      background: linear-gradient(180deg, #ecfdf5, #f8fafc);
      border: 1px solid #bbf7d0;
      border-radius: 0.75rem;

      @include lg-down {
        grid-template-columns: 1fr;
      }
    }

    &__hero-main {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 0;
    }

    &__hero-label {
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    &__hero-value {
      font-size: 2.25rem;
      font-weight: 700;
      line-height: 1.05;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
    }

    &__hero-sub {
      font-size: 0.8125rem;
      color: var(--slate-600);
      font-variant-numeric: tabular-nums;
    }

    &__hero-delta {
      margin-top: 0.25rem;
      font-size: 0.8125rem;
      font-weight: 600;
      font-variant-numeric: tabular-nums;

      &--up {
        color: var(--green-600);
      }
      &--down {
        color: var(--red-600);
      }
      &--neutral {
        color: var(--slate-500);
      }
    }

    // ---------- Coverage chip ----------

    &__coverage {
      display: flex;
      gap: 0.5rem;
      align-items: flex-start;
      padding: 0.625rem 0.875rem;
      border-radius: 0.625rem;
      border: 1px solid transparent;
      max-width: 18rem;

      .icon {
        flex-shrink: 0;
        margin-top: 0.0625rem;
      }

      &--ok {
        background: rgba(16, 185, 129, 0.08);
        border-color: rgba(16, 185, 129, 0.25);
        color: var(--green-700);

        .icon {
          color: var(--green-600);
        }
      }

      &--warn {
        background: var(--amber-50, #fffbeb);
        border-color: var(--amber-200, #fde68a);
        color: var(--amber-700, #b45309);

        .icon {
          color: var(--amber-600, #d97706);
        }
      }

      &--muted {
        background: var(--slate-50);
        border-color: var(--slate-200);
        color: var(--slate-600);

        .icon {
          color: var(--slate-500);
        }
      }
    }

    &__coverage-text {
      display: flex;
      flex-direction: column;
      gap: 0.0625rem;
      min-width: 0;
    }

    &__coverage-title {
      font-size: 0.75rem;
      font-weight: 600;
    }

    &__coverage-detail {
      font-size: 0.6875rem;
      opacity: 0.9;
    }

    // ---------- Trend strip ----------

    &__trend {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 0.625rem;

      @include lg-down {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__cell {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding: 0.75rem 0.875rem;
      background: var(--white);
      border: 1px solid var(--slate-100);
      border-radius: 0.625rem;
      min-width: 0;
      transition: border-color 0.15s ease;

      @include hover {
        border-color: var(--slate-200);
      }
    }

    &__cell-label {
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      @include ellipsis(1);
    }

    &__cell-value {
      font-size: 1.0625rem;
      font-weight: 700;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
      @include ellipsis(1);
    }

    &__cell-delta {
      font-size: 0.75rem;
      font-weight: 600;
      font-variant-numeric: tabular-nums;

      &--up {
        color: var(--green-600);
      }
      &--down {
        color: var(--red-600);
      }
      &--neutral {
        color: var(--slate-500);
      }
    }

    &__cell-delta-subtle {
      font-size: 0.6875rem;
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;
      @include ellipsis(1);
    }
  }
</style>
